<template>
    <div class="mt-4">

        <!-- ============ Main information ============ -->
        <vx-card class="form-card mb-6" :title="$t('resorts.touristic-objects.form.mainTitle')"
            :subtitle="$t('resorts.touristic-objects.form.mainSubtitle')">
            <div class="w-full">
                <vs-input class="border-grey-light w-full" type="text" v-model="touristicObject.resort_reference"
                    :label="$t('resorts.touristic-objects.form.resortReference')" required />

                <vs-checkbox class="ml-0 mb-4 mt-4 text-dark" v-model="touristicObject.published">
                    {{ $t('resorts.touristic-objects.form.published') }}
                </vs-checkbox>

                <div v-if="touristicObjectCategory !== 'events'">
                    <Combobox
                        :label="$t('resorts.touristic-objects.form.typeInput') + ' *'"
                        :placeholder="$t('resorts.touristic-objects.form.type')"
                        :options="typeOptions"
                        v-model="touristicObjectType"
                        @input="changeType" />
                </div>
            </div>

            <div v-if="touristicObjectCategory === 'events'" class="flex flex-wrap gap-4">
                <DatePicker :label="`${$t('resorts.touristic-objects.form.startDate')} *`" v-model="startDateObject" />
                <DatePicker :label="$t('resorts.touristic-objects.form.endDate')" v-model="endDateObject" />
            </div>

            <div v-if="touristicObjectCategory === 'events'" class="flex flex-wrap gap-4 mt-2">
                <DatePicker :label="`${$t('resorts.touristic-objects.form.publishstartDate')} *`" v-model="publishStartDateObject" />
                <DatePicker :label="$t('resorts.touristic-objects.form.publishEndDate')" v-model="publishEndDateObject" />
            </div>

            <div class="w-full mt-4">
                <vs-select class="border-grey-light w-full pb-2" v-model="touristicObject.timeofyear" required
                    :label="$t('timeofyear') + '*'">
                    <vs-select-item :key="option.key" :value="option.key" :text="option.label | capitalize"
                        v-for="option in timeofyearOptions" />
                </vs-select>
            </div>

            <div class="w-full mt-4">
                <Combobox
                    :label="$t('resorts.touristic-objects.form.mainLanguagesInput') + ' *'"
                    :placeholder="$t('resorts.touristic-objects.form.defaultLanguage')"
                    :options="languageOptions"
                    v-model="defaultLanguage" />
            </div>
        </vx-card>

        <!-- ============ Segmentation ============ -->
        <vx-card class="form-card mb-6" v-if="zones || touristicObjectCategory !== 'events'"
            :title="$t('resorts.touristic-objects.form.segmentation')">
            <div class="w-full">
                <div v-if="zones">
                    <Combobox
                        :label="$t('resorts.touristic-objects.form.zoneInput')"
                        :placeholder="$t('resorts.touristic-objects.form.zone')"
                        :options="zoneOptions"
                        cleareable
                        v-model="touristicObjectZone" />
                </div>

                <selectionExpandable class="mt-6" :selections="touristicObject.selections" />
            </div>
        </vx-card>

        <!-- ============ Content ============ -->
        <vx-card v-if="touristicObject.type && touristicObject.default_language" class="form-card form-card--wide mb-6"
            :title="$t('admin.photos.content')">

            <vs-input v-if="isAttributeVisible('address')" class="border-grey-light pb-2 w-full md:max-w-sm mb-4" type="text"
                :value="touristicObject.address" v-model="touristicObject.address"
                :label="$t('resorts.touristic-objects.form.address')" />

            <LocalizedContent ref="localizedContent" v-model="localizedContent"
                :fixedLanguage="(userSpotlioLanguages.find(lang => lang.id === touristicObject.default_language.id)).acronym"
                :fieldsDefinition="([
                { name: 'name', label: `${$t('resorts.touristic-objects.form.name')} *`, type: 'text', maxLength: 50 },
                { name: 'highlight', label: $t('resorts.touristic-objects.form.highlight'), type: 'text', maxLength: 50 },
                { name: 'phone', label: $t('resorts.touristic-objects.form.phone1'), type: 'text', maxLength: 20 },
                { name: 'other_phone', label: $t('resorts.touristic-objects.form.phone2'), type: 'text', maxLength: 20 },
                { name: 'email', label: $t('resorts.touristic-objects.form.email1'), type: 'text', maxLength: 100 },
                { name: 'other_email', label: $t('resorts.touristic-objects.form.email2'), type: 'text', maxLength: 100 },
                { name: 'web_link', label: $t('resorts.touristic-objects.form.website'), type: 'text', maxLength: 65535 },
                { name: 'reservation_link', label: $t('resorts.touristic-objects.form.reservationLink'), type: 'text', maxLength: 65535 },
                { name: 'rates', label: $t('resorts.touristic-objects.form.ratesTitle'), type: 'textarea', maxLength: 65535 },
                { name: 'description', label: $t('resorts.touristic-objects.form.descriptionTitle'), type: 'textarea', maxLength: 65535 },
                { name: 'description_html', label: $t('resorts.touristic-objects.form.descriptionTitle'), type: 'rich_textarea', maxLength: 65535 },
                { name: 'schedule', label: $t('resorts.touristic-objects.form.scheduleTitle'), type: 'textarea', maxLength: 65535 },
                { name: 'services_tags', label: $t('resorts.touristic-objects.form.services'), type: 'tags' },
                { name: 'equipments_tags', label: $t('resorts.touristic-objects.form.equipments'), type: 'tags' },
                { name: 'comfort_tags', label: $t('resorts.touristic-objects.form.comfort'), type: 'tags' },
                { name: 'accessibility_tags', label: $t('resorts.touristic-objects.form.accessibility'), type: 'tags' },
                { name: 'payments_mode_tags', label: $t('resorts.touristic-objects.form.payments'), type: 'tags' },
            ]).filter((localizedAttr) => isLocalizedAttributeVisible(localizedAttr.name))" />
        </vx-card>

        <!-- ============ Requirements ============ -->
        <vx-card class="form-card mb-6" v-if="requirementsVisible" :title="$t('resorts.touristic-objects.form.requirements')">
            <div class="w-full">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <vs-input v-if="isAttributeVisible('min_height')" class="border-grey-light pb-2 w-full" type="number" min="0" step="0.01"
                    :value="touristicObject.min_height" v-model="touristicObject.min_height"
                    :label="$t('resorts.touristic-objects.form.min_height') + ` (${this.$options.filters.unit_system_converter('depth', this.referenceSystem)})`" />

                <vs-input v-if="isAttributeVisible('max_height')" class="border-grey-light pb-2 w-full" type="number" min="0" step="0.01"
                    :value="touristicObject.max_height" v-model="touristicObject.max_height"
                    :label="$t('resorts.touristic-objects.form.max_height') + ` (${this.$options.filters.unit_system_converter('depth', this.referenceSystem)})`" />

                <vs-input v-if="isAttributeVisible('start_height')" class="border-grey-light pb-2 w-full" type="number" min="0" step="0.01"
                    :value="touristicObject.start_height" v-model="touristicObject.start_height"
                    :label="$t('resorts.touristic-objects.form.start_height') + ` (${this.$options.filters.unit_system_converter('height', this.referenceSystem)})`" />

                <vs-input v-if="isAttributeVisible('end_height')" class="border-grey-light pb-2 w-full" type="number" min="0" step="0.01"
                    :value="touristicObject.end_height" v-model="touristicObject.end_height"
                    :label="$t('resorts.touristic-objects.form.end_height') + ` (${this.$options.filters.unit_system_converter('height', this.referenceSystem)})`" />

                <vs-input v-if="isAttributeVisible('min_age')" class="border-grey-light pb-2 w-full" type="number" min="0"
                    :value="touristicObject.min_age" v-model="touristicObject.min_age"
                    :label="$t('resorts.touristic-objects.form.min_age')" />

                <vs-input v-if="isAttributeVisible('tickets')" class="border-grey-light pb-2 w-full" type="number" min="0"
                    :value="touristicObject.tickets" v-model="touristicObject.tickets"
                    :label="$t('resorts.touristic-objects.form.tickets')" />
            </div>

            <div v-if="isAttributeVisible('requirements')" class="mt-2">
                <label class="text-sm pl-1">{{ $t('resorts.touristic-objects.form.requirements') }} </label>
                <v-select class="border-grey-light v-selector" multiple
                    :placeholder="$t('resorts.touristic-objects.form.requirements')"
                    v-model="touristicObject.requirements" :options="requirements" label="key" />
            </div>
            </div>
        </vx-card>

        <!-- ============ Properties ============ -->
        <vx-card class="form-card mb-6" v-if="propertiesVisible" :title="$t('resorts.touristic-objects.form.properties')">
            <div class="w-full">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <vs-input v-if="isAttributeVisible('total_ascent')" class="border-grey-light pb-2 w-full" type="number" min="0" step="0.01"
                    :value="touristicObject.total_ascent" v-model="touristicObject.total_ascent"
                    :label="$t('resorts.touristic-objects.form.total_ascent') + ` (${this.$options.filters.unit_system_converter('height', this.referenceSystem)})`" />

                <vs-input v-if="isAttributeVisible('total_descent')" class="border-grey-light pb-2 w-full" type="number" min="0" step="0.01"
                    :value="touristicObject.total_descent" v-model="touristicObject.total_descent"
                    :label="$t('resorts.touristic-objects.form.total_descent') + ` (${this.$options.filters.unit_system_converter('height', this.referenceSystem)})`" />

                <div v-if="isAttributeVisible('length')" class="pb-2 w-full">
                    <div class="flex items-center gap-1">
                        <label class="text-sm pl-1">
                            {{ $t('resorts.touristic-objects.form.length') }} ({{ this.$options.filters.unit_system_converter('height', this.referenceSystem) }})
                        </label>
                        <SKTooltip :text="$t('resorts.touristic-objects.form.length-info')">
                            <feather-icon icon="InfoIcon" svgClasses="h-4 w-4 text-primary" />
                        </SKTooltip>
                    </div>
                    <vs-input class="border-grey-light w-full" type="number" min="0" step="0.01"
                        :value="touristicObject.length" v-model="touristicObject.length" />
                </div>

                <vs-input v-if="isAttributeVisible('time')" class="border-grey-light pb-2 w-full" type="number" min="0"
                    :value="touristicObject.time" v-model="touristicObject.time"
                    :label="$t('resorts.touristic-objects.form.time')" />

                <vs-input v-if="isAttributeVisible('start_name')" class="border-grey-light pb-2 w-full" type="text"
                    v-model="touristicObject.start_name"
                    :label="$t('resorts.touristic-objects.form.start_name')" />

                <vs-input v-if="isAttributeVisible('end_name')" class="border-grey-light pb-2 w-full" type="text"
                    v-model="touristicObject.end_name"
                    :label="$t('resorts.touristic-objects.form.end_name')" />

                <Combobox v-if="isAttributeVisible('subtype') && subtypes.length > 0"
                    class="pb-2"
                    :label="$t('resorts.touristic-objects.form.subtype')"
                    :options="subtypeOptions"
                    cleareable
                    v-model="touristicObject.subtype_id" />

                <Combobox v-if="isAttributeVisible('ride_thrill')"
                    class="pb-2"
                    :label="$t('resorts.touristic-objects.form.ride_thrill')"
                    :options="rideThrillOptions"
                    cleareable
                    v-model="rideThrill" />

                <Combobox v-if="isAttributeVisible('ride_audience')"
                    class="pb-2"
                    :label="$t('resorts.touristic-objects.form.ride_audience')"
                    :options="rideAudienceOptions"
                    cleareable
                    v-model="rideAudience" />
            </div>

            <vs-checkbox v-if="isAttributeVisible('grooming')" class="ml-0 mb-4 mt-4 text-dark" v-model="touristicObject.grooming">
                {{ $t('resorts.touristic-objects.form.grooming') }}
            </vs-checkbox>

            <div class="w-full mt-2" v-if="isAttributeVisible('facility_difficulty')">
                <label class="text-sm w-full pl-1">{{ this.$t("facilities.tableheader.difficulty") }} </label>
                <v-select class="bg-white search-select rounded-lg w-full md:w-1/2"
                    :options="facilityDifficultySelect" v-model="facilityDifficulty" :appendToBody="true">
                    <template #option="option">
                        <span v-if="option.icon" class="w-8 inline-block text-center">
                            <img class="w-6" :src="option.icon" />
                        </span>
                        {{ option.label }}
                    </template>
                    <template #selected-option="option">
                        <span v-if="option.icon" class="w-8">
                            <img class="w-6" :src="option.icon" />
                        </span>
                        {{ option.label }}
                    </template>
                </v-select>
            </div>
            </div>
        </vx-card>

        <!-- ============ Add image ============ -->
        <vx-card class="form-card form-card--large mb-6" v-if="touristicObject" :title="$t('resorts.touristic-objects.form.addImage')"
            :subtitle="$t('resorts.touristic-objects.form.addImageSub')">
            <ImagesForm :images="touristicObject.image_list" :deletedImages="deletedImages"
                :allowedExtensions="['image/jpg', 'image/jpeg']" id="selectToImage" :validationWidth="1080"
                :validationHeight="575" :validationWeight="5500" :validationMessage="$t('banner.form.imageerror')"
                width="240" height="170" name="selectedImages"></ImagesForm>
        </vx-card>

        <!-- ============ Locate on map ============ -->
        <vx-card class="form-card form-card--large mb-6" :title="$t('resorts.touristic-objects.form.locateMap')"
            :subtitle="$t('resorts.touristic-objects.form.locateMapSub')">
            <vs-checkbox v-model="hasCoordinates" class="checkboxform border-0 mx-0 mb-6">
                {{ this.$t('resorts.touristic-objects.form.need_coords') }}
            </vs-checkbox>

            <MapCoordinates v-if="hasCoordinates" :defaultCoordinates="defaultCoordinates" :componentSize="mapSize"
                @updated="handleMapChange" @invalidCoords="handleInvalidCoords" :zoom=18></MapCoordinates>
        </vx-card>

        <!-- ============ Actions ============ -->
        <div class="flex items-center justify-end gap-4 mt-8">
            <vs-button type="border" class="rounded-lg xs:w-full sm:w-auto text-primary" @click="cancel">
                {{ $t('resorts.touristic-objects.form.cancelButton') }}
            </vs-button>
            <vs-button class="rounded-lg xs:w-full sm:w-auto" @click="save">
                {{ $t('resorts.touristic-objects.form.saveButton') }}
            </vs-button>
        </div>
    </div>
</template>
<script>
import { DateTime } from "luxon";
import { mapGetters } from "vuex";
import store from "@/modules/Shared/Store/store";
import loader from "@/modules/Shared/Mixins/loader";
import Tags from '@/modules/Shared/Components/form/Tags'
import DatePicker from '@/modules/Shared/Components/form/DatePicker.vue';
import { getFacilityDifficulty } from "@/modules/Resorts/Helpers/facilityHelper"
import LocalizedContent from '@/modules/Shared/Components/form/LocalizedContent'
import Combobox from '@/modules/Shared/Components/Combobox.vue'
import SKTooltip from '@/modules/Shared/Components/SKTooltip.vue'
import ImagesForm from '@/modules/Resorts/Pages/touristicObjects/forms/ImagesForm'
import { unifyPaginatedResponse } from "@/modules/Shared/Helpers/apiResponseHelper";
import TouristicObjectServices from '@/modules/Resorts/Services/TouristicObjectServices'
import MapCoordinates from '@/modules/Shared/Components/mapcoordinates/MapCoordinates.vue';
import selectionExpandable from '@/modules/Resorts/Pages/touristicObjects/components/selectionExpandable'
import mapCoordinatesExpandable from '@/modules/Resorts/Pages/touristicObjects/components/mapCoordinatesExpandable'

export default {
    name: 'edit-touristicObject',
    mixins: [loader],
    components: {
        ImagesForm,
        MapCoordinates,
        selectionExpandable,
        mapCoordinatesExpandable,
        DatePicker,
        LocalizedContent,
        Combobox,
        SKTooltip,
        Tags,
    },
    props: {
        create: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
        }
    },
    data() {
        return {
            referenceSystem: 'metric',
            subtypes: [],
            types: [],
            zones: [],
            rideThrills: [],
            rideAudiences: [],
            requirements: [],
            difficultyStyle: 'europe',
            deletedImages: [],
            hasCoordinates: false,
            defaultCoordinates: null,
            mapSize: { width: "w-2/3 md:w-3/3 lg:w-2/2" },
            originalLanguagesIds: [],
            touristicObject: {
                resort_reference: '',
                published: false,
                start_date: '',
                end_date: '',
                publish_start_date: '',
                publish_end_date: '',
                timeofyear: 'winter',
                default_language: null,
                langs_list: [],
                min_height: null,
                max_height: null,
                start_height: null,
                end_height: null,
                total_ascent: null,
                total_descent: null,
                length: null,
                time: null,
                min_age: null,
                tickets: null,
                subtype_id: null,
                map_coordinates: null,
                heights: null,
                image_list: [],
                coordinates: null,
                type: null,
                ride_thrill: null,
                ride_audience: null,
                requirements: [],
                selections: [],
                grooming: null,
                start_name: null,
                end_name: null,
            },
            timeofyearOptions: [
                { key: 'winter', label: this.$t('winter') },
                { key: 'summer', label: this.$t('summer') },
                { key: 'always', label: this.$t('always') },
            ],
            emptyLanguage: {
                name: null,
                height: null,
                phone: null,
                other_phone: null,
                email: null,
                other_email: null,
                web_link: null,
                reservation_link: null,
                rates: null,
                description: null,
                description_html: null,
                schedule: null,
                services_tags: null,
                equipments_tags: null,
                comfort_tags: null,
                accessibility_tags: null,
                payments_mode_tags: null,
            },
        }
    },
    async beforeRouteEnter(to, from, next) {
        if (to.params.create) {
            await store.dispatch('setRouteElement', null)
            next()
            return
        }
        const touristicObject = (await TouristicObjectServices.getTouristicObject(to.params.touristicObjectUuid)).data.object
        if (!touristicObject) {
            next()
            return
        }
        await store.dispatch('setRouteElement', touristicObject)
        next(vm => { vm.touristicObject = JSON.parse(JSON.stringify(touristicObject)) })
    },
    computed: {
        ...mapGetters('resorts', ['resorts']),
        ...mapGetters(['AppActiveUser']),
        ...mapGetters(['userSpotlioLanguages']),
        DateTime() {
            return DateTime
        },
        startDateObject: {
            get() { return this.touristicObject.start_date != '' ? DateTime.fromSQL(this.touristicObject.start_date) : null },
            set(val) { this.touristicObject.start_date = val.toFormat('yyyy-MM-dd HH:mm:ss') }
        },
        endDateObject: {
            get() { return this.touristicObject.end_date != '' ? DateTime.fromSQL(this.touristicObject.end_date) : null },
            set(val) { this.touristicObject.end_date = val.toFormat('yyyy-MM-dd HH:mm:ss') }
        },
        publishStartDateObject: {
            get() { 
                return this.touristicObject.publish_start_date != null ? DateTime.fromSQL(this.touristicObject.publish_start_date) : null 
            },
            set(val) {
                if (val !== null && val !== undefined && val !== '' && val.isValid) {
                    this.touristicObject.publish_start_date = val.toFormat('yyyy-MM-dd HH:mm:ss')
                } else {
                    this.touristicObject.publish_start_date = null
                }
            }
        },
        publishEndDateObject: {
            get() { return this.touristicObject.publish_end_date != null ? DateTime.fromSQL(this.touristicObject.publish_end_date) : null },
            set(val) {
                if (val !== null && val !== undefined && val !== '' && val.isValid) {
                    this.touristicObject.publish_end_date = val.toFormat('yyyy-MM-dd HH:mm:ss')
                } else {
                    this.touristicObject.publish_end_date = null
                }
            }
        },
        typeOptions() {
            return this.types.map((type) => ({ key: type.id, text: type.name }))
        },
        touristicObjectType: {
            get() { return this.touristicObject.type ? this.touristicObject.type.id : null },
            set(val) { this.touristicObject.type = this.types.find(type => type.id == val) }
        },
        zoneOptions() {
            return this.zones.map((zone) => ({ key: zone.uuid, text: zone.name }))
        },
        touristicObjectZone: {
            get() { return this.touristicObject.zone ? this.touristicObject.zone.uuid : null },
            set(val) { this.touristicObject.zone = this.zones.find(type => type.uuid == val) }
        },
        languageOptions() {
            return this.userSpotlioLanguages.map((language) => ({ key: language.id, text: language.name }))
        },
        defaultLanguage: {
            get() { return this.touristicObject.default_language ? this.touristicObject.default_language.id : null },
            set(val) {
                this.touristicObject.default_language = this.userSpotlioLanguages.find(lang => lang.id == val)
                this.addDefaultLanguageToList()
            }
        },
        localizedContent: {
            get() {
                return this.touristicObject.langs_list.filter((content) => this.userSpotlioLanguages.find((lang) => lang.id == content.idioma_id)).map((content) => {
                    return {
                        ...content,
                        language: this.userSpotlioLanguages.find(lang => lang.id == content.idioma_id).acronym
                    }
                })
            },
            set(val) {
                this.touristicObject.langs_list = val.map((content) => {
                    const language = content.language
                    delete content.language;
                    return {
                        ...content,
                        idioma_id: this.findLanguageByAcronym(language).id
                    }
                })
            }
        },
        touristicObjectCategory() { return this.$route.meta.type },
        subtypeOptions() {
            return this.subtypes.map((subtype) => ({ key: subtype.id, text: subtype.name }))
        },
        rideThrillOptions() {
            return this.rideThrills.map((thrill) => ({ key: thrill.id, text: thrill.key }))
        },
        rideAudienceOptions() {
            return this.rideAudiences.map((audience) => ({ key: audience.id, text: audience.key }))
        },
        rideThrill: {
            get() { return this.touristicObject.ride_thrill ? this.touristicObject.ride_thrill.id : null },
            set(val) { this.touristicObject.ride_thrill = this.rideThrills.find(type => type.id == val) }
        },
        rideAudience: {
            get() { return this.touristicObject.ride_audience ? this.touristicObject.ride_audience.id : null },
            set(val) { this.touristicObject.ride_audience = this.rideAudiences.find(type => type.id == val) }
        },
        facilityDifficultySelect() { return getFacilityDifficulty(this.difficultyStyle, this.touristicObject.type.id) },
        facilityDifficulty: {
            get() { return this.touristicObject.facility_difficulty ? this.facilityDifficultySelect.find((item) => item.value === this.touristicObject.facility_difficulty.name) : null },
            set(val) {
                if (!val) {
                    this.touristicObject.facility_difficulty = null
                } else {
                    this.touristicObject.facility_difficulty = { name: val.value  }
                }
            }
        },
        attributePresets() {
            if (!this.types || this.touristicObject.type == null) return []
            const typeFound = this.types.find((type) => type.id == this.touristicObject.type.id)
            console.log(typeFound)
            return typeFound ? typeFound.attributes : []
        },
        localizedAttributePresets() {
            if (!this.types || this.touristicObject.type == null) return []
            const typeFound = this.types.find((type) => type.id == this.touristicObject.type.id)
            return typeFound ? typeFound.localized_attributes : []
        },
        requirementsVisible() {
            return ['min_height', 'max_height', 'start_height', 'end_height', 'min_age', 'tickets', 'requirements']
                .some((attribute) => this.isAttributeVisible(attribute))
        },
        propertiesVisible() {
            return ['total_ascent', 'total_descent', 'length', 'time', 'start_name', 'end_name', 'subtype', 'ride_thrill', 'ride_audience', 'grooming', 'facility_difficulty']
                .some((attribute) => this.isAttributeVisible(attribute))
        },
    },
    async created() {
        await this.load(async () => {
            this.referenceSystem = this.resorts.find(resort => resort.uuid === this.$route.params.uuid).reference_system
            this.difficultyStyle = this.resorts.find(resort => resort.uuid === this.$route.params.uuid).slope_difficulty_referencesystem

            const [touristicObjectTypes, touristicObjectZones, thrills, audiences, requirements] = await Promise.all([
                TouristicObjectServices.getTouristicObjectTypes(),
                unifyPaginatedResponse(TouristicObjectServices.getTouristicObjecZones, this.$route.params.uuid),
                unifyPaginatedResponse(TouristicObjectServices.getRideThrills, { q: `resort_uuid:${this.$route.params.uuid}`, per_page: 30 }),
                unifyPaginatedResponse(TouristicObjectServices.getRideAudiences, { q: `resort_uuid:${this.$route.params.uuid}`, per_page: 30 }),
                unifyPaginatedResponse(TouristicObjectServices.getRequirements, { q: `resort_uuid:${this.$route.params.uuid}`, per_page: 30 }),
            ])
            this.zones = touristicObjectZones
            this.types = this.filterTypes(touristicObjectTypes.data.object)
            this.$set(this.touristicObject, 'subtype_id', this.touristicObject.subtype ? this.touristicObject.subtype.id : null)
            if (this.touristicObject.type) {
                this.loadSubtypes(this.touristicObject.type.id)
            }

            if ((this.touristicObject.category == 'event' || this.$route.meta.type == 'events') && !this.touristicObject.type) {
                this.touristicObject.type = {
                    id: 165,
                }
                this.loadSubtypes(165)
            }

            this.rideThrills = thrills
            this.rideAudiences = audiences
            this.requirements = requirements
            this.touristicObject.image_list.sort((a, b) => b.main - a.main)
            this.hasCoordinates = this.touristicObject.coordinates !== null
            if (this.hasCoordinates) {
                this.defaultCoordinates = { lat: this.touristicObject.coordinates.lat, lon: this.touristicObject.coordinates.lon }
            }
            this.originalLanguagesIds = this.touristicObject.langs_list.map((l) => l.idioma_id)
            if (this.create) {
                this.touristicObject.uuid = this.$route.params.touristicObjectUuid
            }

            if (this.touristicObject && this.referenceSystem === 'imperial') {
                this.touristicObject.min_height = this.$options.filters.value_system_converter(this.touristicObject.min_height, 'imperial', 'depth')
                this.touristicObject.max_height = this.$options.filters.value_system_converter(this.touristicObject.max_height, 'imperial', 'depth')
                this.touristicObject.start_height = this.$options.filters.value_system_converter(this.touristicObject.start_height, 'imperial', 'height')
                this.touristicObject.end_height = this.$options.filters.value_system_converter(this.touristicObject.end_height, 'imperial', 'height')
            }
        })
    },
    methods: {
        filterTypes(types) {
            return types.filter(type =>
                this.$route.meta.type === 'facilities' ? this.isFacilityType(type) : !this.isFacilityType(type)
            ).sort((a, b) => a.name.localeCompare(b.name))
        },
        isFacilityType(type) {
            // Slope, lift, route, area
            return type.id >= 183 && type.id <= 186
        },
        isAttributeVisible(attributeName) {
            if (this.attributePresets.length === 0) return false
            return this.attributePresets.includes(attributeName)
        },
        isLocalizedAttributeVisible(attributeName) {
            if (this.localizedAttributePresets.length === 0) return false
            return this.localizedAttributePresets.includes(attributeName)
        },
        addDefaultLanguageToList() {
            if (!this.findLanguageInListById(this.touristicObject.default_language.id)) {
                this.touristicObject.langs_list.push({
                    ...this.emptyLanguage,
                    idioma_id: this.touristicObject.default_language.id
                })
            }
        },
        findLanguageInListById(id) {
            return this.touristicObject.langs_list.find((l) => l.idioma_id == id)
        },
        findLanguageByAcronym(acronym) {
            return this.userSpotlioLanguages.find((l) => l.acronym == acronym)
        },
        changeType(newType) {
            this.touristicObject.subtype_id = null
            this.loadSubtypes(newType)
        },
        async loadSubtypes(typeId) {
            this.subtypes = []
            if (!typeId) return
            this.subtypes = await unifyPaginatedResponse(TouristicObjectServices.getTouristicObjectSubTypes, {
                q: `type_id:${typeId}`,
                per_page: 30
            })
            this.subtypes = this.subtypes.sort((a, b) => a.name.localeCompare(b.name))
        },
        handleMapChange(eventDataNewCoordinates) {
            this.touristicObject.coordinates = {
                lat: eventDataNewCoordinates.lat,
                lon: eventDataNewCoordinates.lon
            }
        },
        handleInvalidCoords(eventInvalidCoordinates) {
            this.$vs.notify({
                type: 'confirm',
                title: "Error",
                text: `${eventInvalidCoordinates}`,
                color: "danger",
                position: "top-right"
            });
        },
        goBackToList() {
            if (this.touristicObjectCategory == 'events') this.$router.push({ name: 'ToEvents' })
            if (this.touristicObjectCategory == 'services') this.$router.push({ name: 'ToServices' })
            if (this.touristicObjectCategory == 'facilities') this.$router.push({ name: 'ToFacilities' })
        },
        cancel() {
            window.top.postMessage('touristic-object-canceled', '*')
            this.goBackToList()
        },
        save() {
            if(this.create){
                this.createTouristicObject()
            }else{
                this.updateTouristicObject()
            }
        },
        async createTouristicObject() {
            let touristicObject = this.generateTouristicObjectPayload();
            let response = await TouristicObjectServices.createTouristicObject(this.touristicObject.uuid, touristicObject)
            if (response.status == 200 && Object.keys(response.data).length === 0) {
                this.handleTranslations()
                this.handleImages()
                window.top.postMessage('touristic-object-created', '*')
                this.goBackToList()
            }
        },

        async updateTouristicObject() {
            let touristicObject = this.generateTouristicObjectPayload();            
            if (touristicObject != undefined) {
                let response = await TouristicObjectServices.updateTouristicObject(this.touristicObject.uuid, touristicObject)
                if (response.data.code == 200) {
                    this.handleTranslations()
                    this.handleImages()
                    window.top.postMessage('touristic-object-updated', '*')
                }
                this.goBackToList()
            }
        },

        validateService() {
            const { start_date, end_date, timeofyear, weight, default_language, type } = this.touristicObject;
            const errors = [];

            if (default_language == "") {
                errors.push(this.$t('resorts.touristic-objects.form.errors.defaultLanguage'));
            }
            if (timeofyear == "") {
                errors.push(this.$t('resorts.touristic-objects.form.errors.timeofyear'));
            }
            if (type == "") {
                errors.push(this.$t('resorts.touristic-objects.form.errors.type'));
            }

            if (weight === 0 || weight === '') {
                errors.push(this.$t('resorts.touristic-objects.form.errors.weight'));
            }

            if (start_date == "") {
                this.touristicObject.start_date = null
            }

            if (end_date == "") {
                this.touristicObject.end_date = null
            }

            return errors;
        },
        validateEvent() {
            const { start_date, end_date, weight, default_language } = this.touristicObject;
            const errors = [];

            if (default_language == "") {
                errors.push(this.$t('resorts.touristic-objects.form.errors.defaultLanguage'));
            }

            if (start_date == "") {
                errors.push(this.$t('resorts.touristic-objects.form.errors.start_date'));
            }

            if (weight === 0 || weight === '') {
                errors.push(this.$t('resorts.touristic-objects.form.errors.weight'));
            }
            if (end_date == "") {
                errors.push(this.$t('resorts.touristic-objects.form.errors.end_date'));

            }

            return errors;
        },
        validateFacility() {
            let errors = []

            if (this.isSlopeOrRoute() && this.touristicObject.facility_difficulty == null) {
                errors.push(this.$t('resorts.touristic-objects.form.errors.facility_difficulty'));
            }
            return errors;
        },

        isSlopeOrRoute() {
            return this.touristicObject.type.id === 183 || this.touristicObject.type.id === 186
        },

        validate() {
            let errors = []

            if (this.$route.meta.type == 'events') {
                errors = this.validateEvent(this.touristicObject)
            }

            if (this.$route.meta.type == 'services') {
                errors = this.validateService(this.touristicObject)
            }

            if (this.$route.meta.type == 'facilities') {
                errors = this.validateFacility(this.touristicObject)
            }

            return errors;
        },

        getCategory() {
            switch (this.touristicObjectCategory) {
                case 'services':
                    return 'service'
                case 'events':
                    return 'event'
                case 'facilities':
                    return 'facility'
            }
        },

        generateTouristicObjectPayload() {
            let errors = this.validate()

            if (errors.length > 0) {
                this.$vs.notify({
                    type: 'confirm',
                    title: "Error",
                    text: `${errors[0]}`,
                    color: "danger",
                    position: "top-right"
                });
            } else {
                if (this.touristicObject.address == null) this.touristicObject.address = ''
                let defaultLanguage = this.touristicObject.langs_list.find(lang => lang.idioma_id == this.touristicObject.default_language.id)

                const payload = {
                    "uuid": this.touristicObject.uuid,
                    "internal_identifier": this.touristicObject.uuid,
                    "name": defaultLanguage.name,
                    "category": this.getCategory(),
                    "default_language_id": this.touristicObject.default_language.id,
                    "resort_uuid": this.$route.params.uuid,
                    "published": this.touristicObject.published,
                    "weight": this.touristicObject.weight,
                    "timeofyear": this.touristicObject.timeofyear,
                    "selection": this.touristicObject.selection,
                    "selections": this.touristicObject.selections,
                    "resort_reference": this.touristicObject.resort_reference,
                    "coordinates": this.hasCoordinates ? this.touristicObject.coordinates : null,
                    ...(this.touristicObject.type != null ? { 'type_id': this.touristicObject.type.id } : null),
                    ...(this.touristicObject.zone != null ? { 'zone_uuid': this.touristicObject.zone.uuid } : null),
                    ...(this.touristicObject.address != "" ? { 'address': this.touristicObject.address } : null),
                    ...(this.touristicObject.start_date != null ? { 'start_date': this.touristicObject.start_date } : null),
                    ...(this.touristicObject.end_date != null ? { 'end_date': this.touristicObject.end_date } : null),
                    ...(this.touristicObject.publish_start_date != null ? { 'publish_start_date': this.touristicObject.publish_start_date } : null),
                    ...(this.touristicObject.publish_end_date != null ? { 'publish_end_date': this.touristicObject.publish_end_date } : null),
                    "map_coordinates": this.touristicObject.map_coordinates != null ? this.touristicObject.map_coordinates : null,
                    "heights": this.touristicObject.heights != null ? this.touristicObject.heights : null,
                    "subtype_id": this.touristicObject.subtype_id ? this.generateNumberPayloadValue(this.touristicObject.subtype_id) : null,
                    "ride_thrill_id": this.touristicObject.ride_thrill ? this.generateNumberPayloadValue(this.touristicObject.ride_thrill.id) : null,
                    "ride_audience_id": this.touristicObject.ride_audience ? this.generateNumberPayloadValue(this.touristicObject.ride_audience.id) : null,
                    "requirements_ids": this.touristicObject.requirements.length > 0 ? this.touristicObject.requirements.map((r) => r.id) : null,
                    "min_height": this.generateNumberPayloadValue(this.touristicObject.min_height),
                    "max_height": this.generateNumberPayloadValue(this.touristicObject.max_height),
                    "min_age": this.generateNumberPayloadValue(this.touristicObject.min_age),
                    "tickets": this.generateNumberPayloadValue(this.touristicObject.tickets),
                    "length": this.generateNumberPayloadValue(this.touristicObject.length),
                    "start_height": this.generateNumberPayloadValue(this.touristicObject.start_height),
                    "end_height": this.generateNumberPayloadValue(this.touristicObject.end_height),
                    "time": this.generateNumberPayloadValue(this.touristicObject.time),
                    "total_ascent": this.generateNumberPayloadValue(this.touristicObject.total_ascent),
                    "total_descent": this.generateNumberPayloadValue(this.touristicObject.total_descent),
                    "grooming": this.touristicObject.grooming,
                    "start_name": this.touristicObject.start_name,
                    "end_name": this.touristicObject.end_name,
                    ...(
                        this.touristicObject.facility_difficulty && this.getDifficultyIdentifier(this.touristicObject.facility_difficulty.name) !== null
                            ? { "facility_difficulty_id": this.getDifficultyIdentifier(this.touristicObject.facility_difficulty.name) }
                            : null),
                }

                if (this.referenceSystem === 'imperial') {
                    payload.min_height = this.$options.filters.value_system_converter(payload.min_height, 'metric', 'depth')
                    payload.max_height = this.$options.filters.value_system_converter(payload.max_height, 'metric', 'depth')
                    payload.start_height = this.$options.filters.value_system_converter(payload.start_height, 'metric', 'height')
                    payload.end_height = this.$options.filters.value_system_converter(payload.end_height, 'metric', 'height')
                    payload.total_ascent = this.$options.filters.value_system_converter(payload.total_ascent, 'metric', 'height')
                    payload.total_descent = this.$options.filters.value_system_converter(payload.total_descent, 'metric', 'height')
                    payload.length = this.$options.filters.value_system_converter(payload.length, 'metric', 'height')
                }

                return payload
            }
        },
        generateNumberPayloadValue(value) {
            return value === '' || value === null ? null : Number(value)
        },
        getDifficultyIdentifier(difficulty)
        {
            if (this.touristicObject.type.id === 183) {
                switch (difficulty) {
                    case 'novice':
                        return 0
                    case 'easy':
                        return 1
                    case 'intermediate':
                        return 2
                    case 'advanced':
                        return 3
                    case 'expert':
                        return 4
                    case 'freeride':
                        return 5
                    case 'extreme':
                        return 6
                }
            }

            if (this.touristicObject.type.id === 186) {
                switch (difficulty) {
                    case 'low':
                        return 7
                    case 'medium':
                        return 8
                    case 'hard':
                        return 9
                    case 'very_hard':
                        return 10
                }
            }

            return null
        },

        handleTranslations(){
            this.originalLanguagesIds = this.originalLanguagesIds.map((id) => parseInt(id));
            this.touristicObject.langs_list.forEach(async (translation) => {
                const payload = this.generateTouristicObjectTranslationPayload(translation)
                if (this.originalLanguagesIds.includes(parseInt(translation.idioma_id))) {
                    await TouristicObjectServices.updateTouristicObjectTranslation(this.touristicObject.uuid, payload)
                }

                if (!this.originalLanguagesIds.includes(parseInt(translation.idioma_id))) {
                    await TouristicObjectServices.createTouristicObjectTranslation(this.touristicObject.uuid, payload)
                }
            })

            const originalSet = new Set(this.originalLanguagesIds)
            const currentSet = new Set(this.touristicObject.langs_list.map((l) => parseInt(l.idioma_id)))

            originalSet.difference(currentSet).forEach(async (deletedTranslationId) =>{
                await TouristicObjectServices.deleteTouristicObjectTranslation(this.touristicObject.uuid, deletedTranslationId)
            })
        },
        generateTouristicObjectTranslationPayload(translation){
            return {
                name : translation.name,
                highlight: translation.highlight,
                idioma_id : translation.idioma_id,
                web: translation.web_link !== undefined ? translation.web_link : '' ,
                phone: translation.phone !== undefined ? translation.phone : '' ,
                other_phone: translation.other_phone !== undefined ? translation.other_phone : '' ,
                email: translation.email !== undefined ? translation.email : '' ,
                other_email: translation.other_email !== undefined ? translation.other_email : '' ,
                description: translation.description !== undefined ? translation.description : '' ,
                description_html: translation.description_html !== undefined ? translation.description_html : '' ,
                reservation_link: translation.reservation_link !== undefined ? translation.reservation_link : '' ,
                rates: translation.rates != null ? translation.rates : '',
                schedule: translation.schedule != null ? translation.schedule : '',
                services_tags: this.parseTagsValue(translation.services_tags),
                equipments_tags: this.parseTagsValue(translation.equipments_tags),
                payments_mode_tags: this.parseTagsValue(translation.payments_mode_tags),
                comfort_tags: this.parseTagsValue(translation.comfort_tags),
                accessibility_tags: this.parseTagsValue(translation.accessibility_tags),
            }
        },
        parseTagsValue(value) {
            const isNullOrEmptyArray = value === null || (Array.isArray(value) && value.length === 0);
            return isNullOrEmptyArray ? null : value
        },
        handleImages(){
            this.touristicObject.image_list.forEach(async (image) => {
                if(image.new) {
                    await TouristicObjectServices.createTouristicObjectImage(this.touristicObject.uuid, image)
                } else {
                    await TouristicObjectServices.updateTouristicObjectImage(this.touristicObject.uuid, image)
                }
            })
            if(this.deletedImages.length > 0) {
                this.deletedImages.forEach(async (deletedImage) => {
                    await TouristicObjectServices.deleteTouristicObjectImage(this.touristicObject.uuid, deletedImage.uuid)
                })
            }
        },

    },
}
</script>
<style scoped>
.form-card {
    max-width: 30rem;
}

.form-card--large {
    max-width: 60rem;
}

.form-card--wide {
    max-width: none;
}
</style>
<style>
.v-selector .vs__deselect {
    display: none;
}
</style>
